import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { AssociateAuditPlanToProposalsGQL, AssociateAuditPlanToProposalsMutation, AuditPlanColumns, AuditPlanDto, AuditPlanExistsBySurveillancePlanGQL, AuditPlanExistsBySurveillancePlanQuery, AuditPlanQueryComplexInput, AuditPlanQueryInput, AuditPlanStatus, ComparatorOperators, CreateAuditPlanGQL, CreateAuditPlanMutation, CreateAuditPlanningRequest, DeleteAuditPlanGQL, DeleteAuditPlanMutation, DownloadAuditPlansGQL, DownloadAuditPlansMutation, GetAuditPlanLegalEntitiesGQL, GetAuditPlanLegalEntitiesQuery, GetAuditPlansFiltersGQL, GetAuditPlansFiltersQuery, GetAuditPlansGQL, GetAuditPlansQuery, OperatorInput, PagingInput, UpdateAuditPlanGQL, UpdateAuditPlanMutation } from "../../../graphql/generated";

@Injectable({
    providedIn: 'root'
})

export class AuditPlansService {
    private readonly GetAuditPlansGQL: GetAuditPlansGQL = inject(GetAuditPlansGQL);
    private readonly CreateAuditPlanGQL: CreateAuditPlanGQL = inject(CreateAuditPlanGQL);
    private readonly DeleteAuditPlanGQL: DeleteAuditPlanGQL = inject(DeleteAuditPlanGQL);
    private readonly UpdateAuditPlanGQL: UpdateAuditPlanGQL = inject(UpdateAuditPlanGQL);
    private readonly GetAuditPlanLegalEntitiesGQL: GetAuditPlanLegalEntitiesGQL = inject(GetAuditPlanLegalEntitiesGQL);
    private readonly AssociateAuditPlanToProposalsGQL: AssociateAuditPlanToProposalsGQL = inject(AssociateAuditPlanToProposalsGQL)
    private readonly DownloadAuditPlansGQL: DownloadAuditPlansGQL = inject(DownloadAuditPlansGQL)
    private readonly GetAuditPlansFiltersGQL: GetAuditPlansFiltersGQL = inject(GetAuditPlansFiltersGQL)
    private readonly AuditPlanExistsBySurveillancePlanGQL = inject(AuditPlanExistsBySurveillancePlanGQL);

    getAuditPlans(parameters: any, year: number, legalEntityIds: string[]): Observable<GetAuditPlansQuery['getAuditPlanning']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        const legalEntityId = legalEntityIds.map(le => Number(le))
        return this.GetAuditPlansGQL.fetch({ page: param.page, query: param.query, year, legalEntityId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getAuditPlanning))
    }

    createAuditPlan(request: CreateAuditPlanningRequest): Observable<CreateAuditPlanMutation['createAuditPlan']> {
        return this.CreateAuditPlanGQL.mutate({ request }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.createAuditPlan))
    }

    deleteAuditPlan(id: number): Observable<DeleteAuditPlanMutation['deleteAuditPlan']> {
        return this.DeleteAuditPlanGQL.mutate({ id }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.deleteAuditPlan))
    }

    updateAuditPlan(auditPlan: AuditPlanDto): Observable<UpdateAuditPlanMutation['updateAuditPlanning']> {
        const { galtChiefApproval, id, localSuperuserPermission, bodPresentationDate, name, status, legalEntity, notes, surveillancePlan  } = auditPlan
        return this.UpdateAuditPlanGQL.mutate({ galtChiefApproval: galtChiefApproval as boolean, id: Number(id), localSuperUser: localSuperuserPermission as boolean, bodPresentationDate, name, status: ENUM_STATUS[status!], legalEntityId: Number(legalEntity?.id), notes, surveillancePlan: surveillancePlan ?? false }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.updateAuditPlanning))
    }

    getLegalEntities(year: number): Observable<GetAuditPlanLegalEntitiesQuery['getAvailableLegalEntities']> {
        return this.GetAuditPlanLegalEntitiesGQL.fetch({ year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.getAvailableLegalEntities))
    }

    associateAuditPlanToProposals(auditPlanId: number, proposalIds: number[]): Observable<AssociateAuditPlanToProposalsMutation['associateAuditPlanToProposals']> {
        return this.AssociateAuditPlanToProposalsGQL.mutate({ auditPlanId, proposalIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.associateAuditPlanToProposals))
    }

    downloadAuditPlans(timeZone: string, year: number, parameters: any): Observable<DownloadAuditPlansMutation['downloadAuditPlanning']> {
        const param = convertLazyLoadToComplexWithPage(parameters);
        return this.DownloadAuditPlansGQL.mutate({ query: param.query, timeZone, year }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.downloadAuditPlanning))
    }

    getAuditPlansFilters(legalEntityIds: string[], year: number): Observable<GetAuditPlansFiltersQuery['auditPlanFieldsValues']> {
        return this.GetAuditPlansFiltersGQL.fetch({ year, legalEntityIds }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data?.auditPlanFieldsValues))
    }
    auditPlanExistsBySurveillancePlan(legalEntityId: number, year: number, surveillancePlan: boolean, excludedAuditPlanId?: number | null): Observable<AuditPlanExistsBySurveillancePlanQuery['auditPlanExistsBySurveillancePlan']> {
        return this.AuditPlanExistsBySurveillancePlanGQL.fetch({ legalEntityId, year, surveillancePlan, excludedAuditPlanId }, { fetchPolicy: 'no-cache' }).pipe(map(res => res.data.auditPlanExistsBySurveillancePlan));
    }
}

export const ENUM_STATUS = {
    'APPROVED': AuditPlanStatus.Approved,
    'DRAFT': AuditPlanStatus.Draft,
    'NOT_APPROVED': AuditPlanStatus.NotApproved,
    'TO_BE_VALIDATED': AuditPlanStatus.ToBeValidated,
    'VALIDATED': AuditPlanStatus.Validated,
    'NOT_STARTED': AuditPlanStatus.NotStarted
}

export function convertLazyLoadToComplexWithPage(parameters: any): { query: AuditPlanQueryComplexInput, page: PagingInput } {
    const query: AuditPlanQueryInput[] = [];
    if (parameters) {

        if (parameters.filters) {

            Object.entries(parameters.filters).forEach(([field, values]) => {

                if (values !== null && Array.isArray(values) && values.length > 0) {
                    (ENUM_AUDIT_PLANS[field as keyof typeof ENUM_AUDIT_PLANS])?.forEach(key => {

                        query.push({
                            field: key as AuditPlanColumns,
                            filters: {
                                operator: ComparatorOperators.Match,
                                value: key === AuditPlanColumns.AuditPlanStatus ? values.map(v => mapStatus(v)) : key === AuditPlanColumns.LegalEntityCode ? values.map(v => v?.split(/\s-\s(.+)/)[0]?.trim()) : values.map(v => v?.toLowerCase() === 'empty' ? null : v?.toLowerCase() === 'not validated' || v?.toLowerCase() === 'not approved' || v?.toLowerCase() === 'no' ? false : v?.toLowerCase() === 'validated' || v?.toLowerCase() === 'approved' || v?.toLowerCase() === 'yes' ? true : v)
                            }
                        });
                    })
                }
            });
        }
    }
    return {
        query: {
            operator: OperatorInput.And,
            query: [
                {
                    query: query,
                    operator: OperatorInput.And
                }
            ]
        },
        page: {
            pageNo: parameters?.page ?? 0,
            pageSize: parameters?.rows ?? 1000000
        }
    }
}

export const ENUM_AUDIT_PLANS = {
    name: [AuditPlanColumns.AuditPlanName],
    legalEntityName: [AuditPlanColumns.LegalEntityCode],
    bodPresentationDate: [AuditPlanColumns.BodPresentationDate],
    attachment: [AuditPlanColumns.AuditAttachmentsQuantity],
    localSuperuserPermission: [AuditPlanColumns.AuditLocalSuperUserApproval],
    galtChiefApproval: [AuditPlanColumns.AuditGaltChiefApproval],
    status: [AuditPlanColumns.AuditPlanStatus],
    notes: [AuditPlanColumns.AuditPlanNotes],
    businessUnit: [AuditPlanColumns.BusinessUnit],
    organizationalUnit: [AuditPlanColumns.OrganizationalUnit],
    validatorName: [AuditPlanColumns.AuditPlanValidatorName],
    validatorDate: [AuditPlanColumns.AuditPlanValidatorDate],
    approverName: [AuditPlanColumns.AuditPlanApproverName],
    approverDate: [AuditPlanColumns.AuditPlanApproverDate],
    surveillancePlan: [AuditPlanColumns.AuditPlanSurveillancePlan]
}

export function mapStatus(value: string) {
    if (value === 'APPROVED') {
        return AuditPlanStatus.Approved
    }
    else if (value === 'DRAFT') {
        return AuditPlanStatus.Draft
    }
    else if (value === 'NOT APPROVED') {
        return AuditPlanStatus.NotApproved
    }
    else if (value === 'NOT STARTED') {
        return AuditPlanStatus.NotStarted
    }
    else if (value === 'TO BE VALIDATED') {
        return AuditPlanStatus.ToBeValidated
    }
    else if (value === 'VALIDATED') {
        return AuditPlanStatus.Validated
    }
    else {
        return AuditPlanStatus.Approved
    }
}